import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, LOGO_URL } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="login-page">
      <div
        className="hidden w-1/2 bg-cover bg-center lg:block"
        style={{ backgroundImage: "linear-gradient(rgba(10,10,10,0.55), rgba(10,10,10,0.9)), url('https://images.unsplash.com/photo-1747930838451-1f9c3a8ea057?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHx0b3clMjB0cnVjayUyMGhpZ2h3YXl8ZW58MHx8fHwxNzg2MjU2MzUwfDA&ixlib=rb-4.1.0&q=85')" }}
      >
        <div className="flex h-full flex-col justify-end p-12">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-primary">Operations Portal</p>
          <h2 className="mt-4 max-w-md font-display text-3xl font-black tracking-tight text-white">
            Dispatch. Evidence. Invoices. One system.
          </h2>
        </div>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-md animate-fade-up">
          <Link to="/" className="mb-10 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground transition-colors hover:text-primary" data-testid="back-home-link">
            <ArrowLeft size={14} /> Back to site
          </Link>
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="PSS Towing Auto Assist" className="h-14 w-14 rounded-sm object-contain" data-testid="login-logo" />
            <div>
              <h1 className="font-display text-2xl font-black tracking-tight">PSS Towing</h1>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">Auto Assist Portal</p>
            </div>
          </div>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@psstowing.com"
                className="rounded-sm border-input bg-card py-6 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-sm border-input bg-card py-6 focus-visible:ring-primary"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="rounded-sm border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-red-400">{error}</p>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full rounded-sm bg-primary py-6 text-sm font-bold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-amber-400 hover:text-black"
            >
              {busy ? "Authenticating..." : "Sign In"}
            </Button>
          </form>
          <p className="mt-8 text-center text-xs text-muted-foreground">
            Accounts are provisioned by your system administrator (Dev role).
          </p>
        </div>
      </div>
    </div>
  );
}
